import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { CopyIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Autocomplete, DatePicker, useApiMutation } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { EventErrorMessages, EventType } from '@/modules/events/types';

import {
  EventStatusEnum,
  eventStatusOptions,
  secretKeysOptions,
} from '../../../constants';
import { ErrorMessagePopover } from '../../error-message-popover';

interface InfoTabProps {
  errorMessages?: EventErrorMessages | null;
}

export const InfoTab = ({ errorMessages }: InfoTabProps) => {
  const params = useParams<{ id: string }>();

  const { control, watch } = useFormContext();
  const status = watch('status');

  const queryClient = useQueryClient();

  const eventTypes = queryClient.getQueryData<EventType[]>(['eventTypes']);

  const typesOptions = eventTypes?.map((type) => ({
    id: type.name,
    value: type.id,
  }));

  const refreshCredentialsMutation = useApiMutation<void>(
    `/events/${params.id}/credentials/refresh`,
    {
      method: 'POST',
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['eventDetails', params.id],
        });
        toast.success('Credentials refreshed');
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const copyValue = (value: string) => {
    if (value) {
      navigator.clipboard.writeText(value).then(() => {
        toast('Copied to clipboard');
      });
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-zinc-50">Basic Info</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <MultiSelect
            name="types"
            label="Type"
            control={control}
            placeholder="Select event types"
            options={typesOptions}
            maxCount={4}
            requiredMark
          />
          <FormField
            control={control}
            name="name"
            render={({ field, formState }) => (
              <FormItem>
                <FormLabel>
                  Name<span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Add event name"
                    {...field}
                    error={!!formState.errors['name']}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DatePicker
            control={control}
            name="startDate"
            label="Start Date"
            requiredMark
          />

          <Autocomplete
            name="status"
            label="Status"
            control={control}
            placeholder="Select status"
            options={eventStatusOptions}
            className={cn({
              'text-green-500 hover:text-green-500':
                status === EventStatusEnum.Active,
              'text-accent hover:text-accent':
                status === EventStatusEnum.Archived,
            })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {secretKeysOptions.map((formField) => (
            <FormField
              control={control}
              name={formField.formKey}
              key={formField.formKey}
              render={({ field }) => (
                <FormItem className={cn(!field.value && 'cursor-not-allowed')}>
                  <FormLabel className={cn(!field.value && 'cursor-not-allowed')}>
                    {formField.label}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        readOnly
                        className={cn(
                          'pr-10 focus-visible:ring-0',
                          !field.value && 'cursor-not-allowed'
                        )}
                        placeholder="generates automatically"
                        {...field}
                      />
                      <div
                        data-testid="copy-button"
                        className={cn(
                          'absolute right-2 top-[7px] cursor-not-allowed',
                          field.value && 'cursor-pointer'
                        )}
                        onClick={() => copyValue(field.value)}
                      >
                        <CopyIcon
                          size={30}
                          color={
                            field.value ? colors.zinc[200] : colors.zinc[800]
                          }
                        />
                      </div>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          ))}
          {params.id && (
            <Button
              variant="outline"
              className="max-w-[150px] self-end h-11"
              isLoading={refreshCredentialsMutation.isPending}
              onClick={() => refreshCredentialsMutation.mutate()}
              startIcon={<ArrowClockwiseIcon size={20} />}
            >
              Refresh keys
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-zinc-50">Feature Flags</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Remixing</h3>
            <FormField
              control={control}
              name={'remixingEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'remixingEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'remixingEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Group Ordering</h3>
            <FormField
              control={control}
              name={'groupOrderingEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'groupOrderingEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'groupOrderingEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Generated Image Code</h3>
            <FormField
              control={control}
              name={'isGeneratedImageCodeEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'isGeneratedImageCodeEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'isGeneratedImageCodeEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Promo SMS</h3>
            <FormField
              control={control}
              name={'isPromoSmsEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'isPromoSmsEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'isPromoSmsEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Surprise Me</h3>
            <FormField
              control={control}
              name={'surpriseMeEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'surpriseMeEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'surpriseMeEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Rectangle Image Generation</h3>
            <FormField
              control={control}
              name={'rectangleImageGenerationEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'rectangleImageGenerationEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'rectangleImageGenerationEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <h3 className="text-sm font-semibold text-zinc-50">Faded Heel</h3>
            <FormField
              control={control}
              name={'isFadedHeelEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'isFadedHeelEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'isFadedHeelEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
          <div className="gap-3 flex flex-col">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-zinc-50">
                Shoe Sizes Hint
              </h3>
              <ErrorMessagePopover
                message={errorMessages?.shoeSizesHintMessage}
              />
            </div>
            <FormField
              control={control}
              name={'isShoeSizeHintEnabled'}
              render={({ field }) => (
                <FormControl>
                  <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                    <label
                      htmlFor={'isShoeSizeHintEnabled'}
                      className="cursor-pointer flex items-center gap-2 text-zinc-100"
                    >
                      <span
                        className={cn(
                          'text-sm mr-auto',
                          field.value ? 'text-zinc-50' : 'text-zinc-400'
                        )}
                      >
                        {field.value ? 'Enable' : 'Disable'}
                      </span>
                      <Switch
                        id={'isShoeSizeHintEnabled'}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </label>
                  </div>
                </FormControl>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
